import React, { createContext, useState, useContext, useEffect } from 'react';
import * as api from '../services/api';
import config from '../config';
import { cacheService, CACHE_KEYS, CACHE_TTL } from '../utils/cacheService';

const ParentAgentContext = createContext(null);

export const ENVIRONMENTS = {
  PRODUCTION: 'production'
};

export function useParentAgents() {
  const context = useContext(ParentAgentContext);
  if (!context) {
    throw new Error('useParentAgents must be used within a ParentAgentProvider');
  }
  return context;
}

export function ParentAgentProvider({ children }) {
  const [parentAgents, setParentAgents] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState({ loaded: false, fromCache: false });
  const activeEnvironment = ENVIRONMENTS.PRODUCTION;

  // OPTIMIZATION 3: PARALLELIZED CONTEXT INITIALIZATION
  useEffect(() => {
    const fetchParentAgents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🚀 ParentAgentContext: Starting initialization...');
        const startTime = performance.now();
        
        // Try to get from cache first
        const cachedData = cacheService.get(CACHE_KEYS.PARENT_AGENTS);
        
        if (cachedData) {
          console.log('📦 ParentAgentContext: Loading from cache');
          setParentAgents(cachedData);
          setCacheStatus({ loaded: true, fromCache: true });
          setLoading(false);
          
          // Fetch fresh data in background (non-blocking)
          fetchFreshData().catch(err => 
            console.warn('Background refresh failed:', err)
          );
          
          const endTime = performance.now();
          console.log(`✅ ParentAgentContext: Loaded from cache in ${(endTime - startTime).toFixed(2)}ms`);
          return;
        }
        
        // If not in cache, fetch from API
        await fetchFreshData();
        
        const endTime = performance.now();
        console.log(`✅ ParentAgentContext: Initialized in ${(endTime - startTime).toFixed(2)}ms`);
        
      } catch (err) {
        console.error('Error in fetchParentAgents:', err);
        setError(err.message || 'Failed to load parent agents');
        setLoading(false);
      }
    };

    const fetchFreshData = async () => {
      try {
        console.log('📡 ParentAgentContext: Fetching fresh data from API...');
        const response = await api.getParentAgents();
        
        if (response && response.agents && Array.isArray(response.agents)) {
          const formattedAgents = {};
          
          response.agents.forEach(agent => {
            if (!agent) return;
            
            const agentId = agent.uniqueIdentifier || agent.id;
            const agentName = agent.agentName || agent.repoName;
            
            if (!agentId) {
              console.warn('Skipping agent without ID:', agent);
              return;
            }
            
            formattedAgents[agentId] = {
              id: agentId,
              name: agentName || `Agent ${agentId}`,
              description: agent.agentDescription || agent.description || '',
              prefix: agent.repoName || '',
              environments: {
                [ENVIRONMENTS.PRODUCTION]: {
                  subdomain: agentId.length >= 2 ? `f${agentId.substring(0, 2)}ij0peld` : 'default',
                  apiUrl: config.api.endpoint,
                }
              },
              config: {
                region: agent.awsRegion || config.region || config.aws?.defaultRegion || 'us-west-2',
                cognito: {
                  userPoolId: config.cognito?.userPoolId || '',
                  userPoolClientId: config.cognito?.userPoolWebClientId || '',
                  identityPoolId: config.cognito?.identityPoolId || '',
                  region: config.cognito?.region || config.region || 'us-west-2'
                }
              },
              agents: []
            };
          });
          
          console.log(`✅ ParentAgentContext: Formatted ${Object.keys(formattedAgents).length} agents`);
          setParentAgents(formattedAgents);
          
          // Cache the data for 5 minutes
          cacheService.set(CACHE_KEYS.PARENT_AGENTS, formattedAgents, CACHE_TTL.MEDIUM);
          setCacheStatus({ loaded: true, fromCache: false });
        } else {
          console.warn('Empty or invalid response for parent agents:', response);
          setParentAgents({});
        }
      } catch (err) {
        console.error('Error fetching fresh parent agents:', err);
        // Only set error if we don't have cached data
        if (!cacheStatus.fromCache) {
          setError(err.message || 'Failed to load parent agents');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchParentAgents();
  }, []);

  const refreshParentAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 ParentAgentContext: Refreshing...');
      
      // Clear cache
      cacheService.remove(CACHE_KEYS.PARENT_AGENTS);
      
      // Fetch fresh data
      const response = await api.getParentAgents();
      
      if (response && response.agents && Array.isArray(response.agents)) {
        const formattedAgents = {};
        
        response.agents.forEach(agent => {
          if (!agent) return;
          
          const agentId = agent.uniqueIdentifier || agent.id;
          const agentName = agent.agentName || agent.repoName;
          
          if (!agentId) {
            console.warn('Skipping agent without ID:', agent);
            return;
          }
          
          formattedAgents[agentId] = {
            id: agentId,
            name: agentName || `Agent ${agentId}`,
            description: agent.agentDescription || agent.description || '',
            prefix: agent.repoName || '',
            environments: {
              [ENVIRONMENTS.PRODUCTION]: {
                subdomain: agentId.length >= 2 ? `f${agentId.substring(0, 2)}ij0peld` : 'default',
                apiUrl: config.api.endpoint,
              }
            },
            config: {
              region: agent.awsRegion || config.region || config.aws?.defaultRegion || 'us-west-2',
              cognito: {
                userPoolId: config.cognito?.userPoolId || '',
                userPoolClientId: config.cognito?.userPoolWebClientId || '',
                identityPoolId: config.cognito?.identityPoolId || '',
                region: config.cognito?.region || config.region || 'us-west-2'
              }
            },
            agents: []
          };
        });
        
        setParentAgents(formattedAgents);
        cacheService.set(CACHE_KEYS.PARENT_AGENTS, formattedAgents, CACHE_TTL.MEDIUM);
        setCacheStatus({ loaded: true, fromCache: false });
        
        console.log('✅ ParentAgentContext: Refreshed successfully');
      }
    } catch (err) {
      console.error('Error refreshing parent agents:', err);
      setError(err.message || 'Failed to refresh parent agents');
    } finally {
      setLoading(false);
    }
  };

  const getParentAgentConfig = (parentAgent) => {
    if (!parentAgent) return null;
    return {
      ...parentAgent.config,
      apiUrl: parentAgent.environments?.[ENVIRONMENTS.PRODUCTION]?.apiUrl || config.api.endpoint
    };
  };

  const getParentAgentForAgent = (fullAgentId) => {
    if (!fullAgentId) return null;
    const [prefix] = fullAgentId.split('-');
    
    for (const [parentKey, parent] of Object.entries(parentAgents)) {
      if (parent && parent.prefix === prefix) {
        return { ...parent, key: parentKey };
      }
    }
    return null;
  };

  const getBaseAgentId = (fullAgentId) => {
    if (!fullAgentId) return null;
    const [, baseId] = fullAgentId.split('-');
    return baseId;
  };

  const getFullAgentId = (parentAgent, baseId) => {
    if (!parentAgent || !baseId) return null;
    return `${parentAgent.prefix}-${baseId}`;
  };

  const getSubdomain = (parentAgent) => {
    if (!parentAgent) return null;
    return parentAgent.environments?.[ENVIRONMENTS.PRODUCTION]?.subdomain || null;
  };

  const getApiUrl = (subdomain) => {
    return config.api.endpoint;
  };

  const value = {
    parentAgents,
    loading,
    error,
    cacheStatus,
    getParentAgentConfig,
    getParentAgentForAgent,
    getBaseAgentId,
    getFullAgentId,
    getSubdomain,
    getApiUrl,
    refreshParentAgents,
    activeEnvironment
  };

  return (
    <ParentAgentContext.Provider value={value}>
      {children}
    </ParentAgentContext.Provider>
  );
}